import React, { useState, useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api";
import { showGlobalModal } from "utils/modalEmitter";
import { Tooltip } from "@material-ui/core";
import "./Header.css";

const TOUR_STEPS = [
  {
    id: "/dashboard",
    label: "Dashboard",
    description:
      "Página inicial com visão geral da sua conta: projetos recentes, consumo de armazenamento e atalhos para as principais áreas."
  },
  {
    id: "/projects",
    label: "Meus Projetos",
    description:
      "Lista todos os projetos da sua conta. A partir daqui você acessa fotos, vídeos, gêmeos digitais, tours 360, GeoView e relatórios de cada projeto."
  },
  {
    id: "/plans",
    label: "Planos e assinatura",
    description:
      "Visualize o plano atual, compare limites de uso e faça upgrade da assinatura conforme a necessidade da sua operação."
  }
];

const Header = props => {
  const { drawerMiniMethod, mini, token } = props;
  const navigate = useNavigate();
  const [username, setUsername] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (!token) {
      setUsername(null);
      setTrialDaysLeft(null);
      return;
    }
    let cancelled = false;
    api
      .get("/users/logged", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (!cancelled && data) {
          setUsername(data.name || data.username || null);
          const sub = data.subscription_status;
          const paid =
            sub === "active" || sub === "trialing" || sub === "pending_cancel";
          if (data.trial_ends_at && !paid) {
            const end = new Date(data.trial_ends_at);
            const now = new Date();
            if (end > now) {
              setTrialDaysLeft(Math.max(1, Math.ceil((end - now) / 86400000)));
            } else {
              setTrialDaysLeft(null);
            }
          } else {
            setTrialDaysLeft(null);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setUsername(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const [menuClicked, setMenuClicked] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifList, setNotifList] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  const [transferLoading, setTransferLoading] = useState({});

  const currentTourStep = TOUR_STEPS[tourStepIndex] || null;
  const profileRef = useRef(null);
  const notifRef = useRef(null);
  const tourHighlightElRef = useRef(null);
  const [tourTargetRect, setTourTargetRect] = useState(null);
  const location = useLocation();

  const collapseIcon = isMobile ? menuClicked : !mini;

  useEffect(() => {
    if (!tourActive || !currentTourStep) {
      setTourTargetRect(null);
      if (tourHighlightElRef.current) {
        tourHighlightElRef.current.classList.remove("tour-step-highlight");
        tourHighlightElRef.current = null;
      }
      return;
    }

    const id = currentTourStep.id;
    const t = setTimeout(() => {
      // Marketplace é uma seção (item pai); etapas order-quote e pilot-registration destacam o pai
      let el = null;
      if (id === "/order-quote" || id === "/pilot-registration") {
        el = document.querySelector('[data-tour-id="marketplace"]');
      } else {
        el = document.querySelector(`[data-tour-id="${id}"]`);
      }

      if (!el) {
        if (tourHighlightElRef.current) {
          tourHighlightElRef.current.classList.remove("tour-step-highlight");
          tourHighlightElRef.current = null;
        }
        setTourTargetRect(null);
        return;
      }

      if (tourHighlightElRef.current && tourHighlightElRef.current !== el) {
        tourHighlightElRef.current.classList.remove("tour-step-highlight");
      }

      tourHighlightElRef.current = el;
      el.classList.add("tour-step-highlight");
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });

      if (el && el.isConnected) {
        const rect = el.getBoundingClientRect();
        if (!rect.width || !rect.height) {
          setTourTargetRect(null);
        } else {
          setTourTargetRect(rect);
        }
      }
    }, 150);

    return () => {
      clearTimeout(t);
      if (tourHighlightElRef.current) {
        tourHighlightElRef.current.classList.remove("tour-step-highlight");
        tourHighlightElRef.current = null;
      }
      setTourTargetRect(null);
    };
  }, [tourActive, tourStepIndex]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setMenuClicked(false);
      setProfileOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMenuClicked(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (profileOpen || notifOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [profileOpen, notifOpen]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setNotifLoading(true);
    try {
      const { data } = await api.get("/notifications", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifList(Array.isArray(data) ? data : []);
    } catch {
      setNotifList([]);
    } finally {
      setNotifLoading(false);
    }
  }, [token]);

  // Carrega notificações assim que o usuário tiver token,
  // para que o indicador (badge) apareça mesmo antes de abrir o menu.
  useEffect(() => {
    if (token) fetchNotifications();
  }, [token, fetchNotifications]);

  useEffect(() => {
    if (notifOpen && token) fetchNotifications();
  }, [notifOpen, token, fetchNotifications]);

  const handleNotifToggle = () => {
    setNotifOpen(prev => !prev);
  };

  const markNotifRead = async (e, userNotifId) => {
    e.stopPropagation();
    if (!token) return;
    try {
      await api.patch(
        `/notifications/${userNotifId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifList(prev =>
        prev.map(n => (n.id === userNotifId ? { ...n, read_at: new Date().toISOString() } : n))
      );
    } catch {}
  };

  const deleteNotif = async (e, userNotifId) => {
    e.stopPropagation();
    if (!token) return;
    try {
      await api.delete(`/notifications/${userNotifId}`, {
        headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifList(prev => prev.filter(n => n.id !== userNotifId));
    } catch {}
  };

  const handleTransferAction = async (e, notifId, transferId, action) => {
    e.stopPropagation();
    if (!token) return;
    const key = `${transferId}-${action}`;
    setTransferLoading(prev => ({ ...prev, [key]: true }));
    try {
      await api.post(
        `/transfers/${action}`,
        { transfer_id: transferId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifList(prev =>
        prev.map(n => (n.id === notifId ? { ...n, read_at: new Date().toISOString() } : n))
      );
      const msg = action === "accept"
        ? "Projeto transferido com sucesso! Ele já aparece nos seus projetos."
        : "Transferência recusada.";
      showGlobalModal({
        variant: action === "accept" ? "success" : "info",
        title: action === "accept" ? "Aceito!" : "Recusado",
        message: msg,
      });
    } catch (err) {
      const detail = err.response?.data?.detail || "Ocorreu um erro. Tente novamente.";
      showGlobalModal({ variant: "error", title: "Erro", message: detail });
    } finally {
      setTransferLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleMenuClick = () => {
    setMenuClicked(!menuClicked);
    drawerMiniMethod();
  };

  const options = [
    {
      icon: "fas fa-credit-card",
      label: "Pagamento",
      to: "/subscriptions"
    },
    {
      icon: "fas fa-lock",
      label: "Configurações",
      to: "/alterar-senha"
    },
    {
      icon: "fas fa-sign-out-alt",
      label: "Encerrar sessão",
      to: "/logout"
    }
  ];

  const handleOptionClick = to => {
    setProfileOpen(false);
    navigate(to);
  };

  return (
    <div className="header-toolbar-wrap">
      {trialDaysLeft != null && (
        <div className="trial-banner" role="status">
          <span>
            Período de testes: <strong>{trialDaysLeft}</strong>{" "}
            {trialDaysLeft === 1 ? "dia restante" : "dias restantes"}.
          </span>
          <button type="button" className="trial-banner__btn" onClick={() => navigate("/plans")}>
            Ver planos
          </button>
        </div>
      )}
    <div
      className={`navbar-container text-white d-flex ${
        isMobile
          ? menuClicked
            ? "justify-content-end"
            : "justify-content-between"
          : "justify-content-between"
      } align-items-center ps-0 pe-3 py-2`}
    >
      <button
        className="btn menu-hamb"
        onClick={handleMenuClick}
        title={collapseIcon ? "Recolher menu" : "Abrir menu"}
      >
        <i className="fas fa-bars" />
      </button>

      {!(menuClicked && window.innerWidth < 360) && (
        <div className="header-actions">
          <div className="header-notif-wrap" ref={notifRef}>
            <Tooltip title="Notificações — Ver alertas e transferências de projeto" arrow placement="bottom">
              <button
                type="button"
                className={`btn header-icon-btn header-icon-btn--notif ${notifOpen ? "active" : ""}`}
                onClick={handleNotifToggle}
                aria-expanded={notifOpen}
              >
                <i className="fas fa-bell" />
                {notifList.some(n => !n.read_at) && (
                  <span className="header-notif-badge" aria-hidden="true" />
                )}
              </button>
            </Tooltip>
            {notifOpen && (
              <div className="header-notif-dropdown">
                <div className="header-notif-dropdown-title">Notificações</div>
                {notifLoading ? (
                  <div className="header-notif-loading">
                    <span className="spinner-border spinner-border-sm" />
                  </div>
                ) : notifList.length === 0 ? (
                  <div className="header-notif-empty">Nenhuma notificação.</div>
                ) : (
                  <ul className="header-notif-list">
                    {notifList.map(n => (
                      <li
                        key={n.id}
                        className={`header-notif-item ${n.read_at ? "" : "unread"}`}
                      >
                        <div className="header-notif-item-content">
                          <strong>{n.title}</strong>
                          <p>{n.body}</p>
                          {n.action_type === "project_transfer" && n.action_id && !n.read_at && (
                            <div className="header-notif-transfer-actions">
                              <button
                                type="button"
                                className="header-notif-transfer-btn --accept"
                                disabled={transferLoading[`${n.action_id}-accept`] || transferLoading[`${n.action_id}-reject`]}
                                onClick={e => handleTransferAction(e, n.id, n.action_id, "accept")}
                              >
                                <i className="fas fa-check-circle" /> Aceitar
                              </button>
                              <button
                                type="button"
                                className="header-notif-transfer-btn --reject"
                                disabled={transferLoading[`${n.action_id}-accept`] || transferLoading[`${n.action_id}-reject`]}
                                onClick={e => handleTransferAction(e, n.id, n.action_id, "reject")}
                              >
                                <i className="fas fa-times-circle" /> Recusar
                              </button>
                            </div>
                          )}
                          <div className="header-notif-item-footer">
                            <time>{new Date(n.created_at).toLocaleDateString("pt-BR")}</time>
                            <div className="header-notif-item-actions">
                              <button
                                type="button"
                                className="header-notif-icon-btn"
                                title="Marcar como lido"
                                onClick={e => markNotifRead(e, n.id)}
                                aria-label="Marcar como lido"
                              >
                                <i className="fas fa-check" />
                              </button>
                              <button
                                type="button"
                                className="header-notif-icon-btn header-notif-icon-btn--delete"
                                title="Excluir"
                                onClick={e => deleteNotif(e, n.id)}
                                aria-label="Excluir"
                              >
                                <i className="fas fa-trash-alt" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <div ref={profileRef} className="header-actions">
          <Tooltip title="Ajuda — Tour guiado pelo menu" arrow placement="bottom">
            <button
              type="button"
              className="btn header-icon-btn header-icon-btn--help"
              onClick={() => {
                setTourStepIndex(0);
                setTourActive(true);
              }}
              aria-expanded={tourActive}
            >
              <i className="fas fa-question-circle" />
            </button>
          </Tooltip>
          <div className="header-profile-wrap">
            <Tooltip title={username ? `Olá, ${username}! Perfil e opções` : "Perfil e opções"} arrow placement="bottom">
              <button
                type="button"
                className="btn header-profile-btn"
                onClick={() => setProfileOpen(!profileOpen)}
                aria-expanded={profileOpen}
              >
                <i className="fas fa-user" />
              </button>
            </Tooltip>
            {profileOpen && (
              <div className="header-profile-dropdown">
                {options.map((opt, i) => (
                  <button
                    key={i}
                    type="button"
                    className="header-profile-option"
                    onClick={() => handleOptionClick(opt.to)}
                  >
                    <i className={opt.icon} />
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>
      )}
    </div>
      {tourActive && currentTourStep && tourTargetRect && (
        <div className="header-tour-overlay" role="dialog" aria-modal="true" aria-label="Tour do menu">
          <div
            className="header-tour-backdrop"
            onClick={() => setTourActive(false)}
            aria-hidden="true"
          />
          <div
            className="header-tour-spotlight"
            style={{
              left: tourTargetRect.left,
              top: tourTargetRect.top,
              width: tourTargetRect.width,
              height: tourTargetRect.height
            }}
            aria-hidden="true"
          />
          <div
            className="header-tour-tooltip"
            style={{
              left: `${tourTargetRect.right + 16}px`,
              top: `${tourTargetRect.top}px`
            }}
          >
            <div className="header-tour-badge">
              Passo {tourStepIndex + 1} de {TOUR_STEPS.length}
            </div>
            <h3 className="header-tour-title">{currentTourStep.label}</h3>
            <p className="header-tour-text">{currentTourStep.description}</p>
            <div className="header-tour-actions">
              <button
                type="button"
                className="btn header-tour-secondary"
                onClick={() => setTourActive(false)}
              >
                Pular tour
              </button>
              <div>
                {tourStepIndex > 0 && (
                  <button
                    type="button"
                    className="btn header-tour-secondary me-2"
                    onClick={() => setTourStepIndex(prev => Math.max(0, prev - 1))}
                  >
                    Anterior
                  </button>
                )}
                <button
                  type="button"
                  className="btn header-tour-primary"
                  onClick={() => {
                    if (tourStepIndex < TOUR_STEPS.length - 1) {
                      setTourStepIndex(prev => prev + 1);
                    } else {
                      setTourActive(false);
                    }
                  }}
                >
                  {tourStepIndex < TOUR_STEPS.length - 1 ? "Próximo" : "Concluir"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  token: state.auth?.accessToken ?? null
});

export default connect(mapStateToProps)(Header);
